import { Alert, Button, Layout, Skeleton } from 'antd';
import ResellerSider from './ResellerSider';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import './ResellerDashboard.scss';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import ResellerPromo from './ResellerPromo';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { getResellerLogo } from '@/api/unbrandedReceptionist';
import { useQuery } from '@tanstack/react-query';
import { Loading } from '@/components/Loading';
import { useDispatch } from 'react-redux';
import { updateReseller } from '@/store/reseller/slice';
import ResellerPendingPaymentPromo from './ResellerPenfdingPaymentPromo';
import ClearPaymentModal from '@/components/ClearPaymentModal/ClearPaymentModal';
import ResellerSubCancelPromo from './ResellerSubCancelPromo';
import { setAppTheme } from '@/store/app/slice';
import { MenuOutlined } from '@ant-design/icons';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import { applyTranslation } from '@/utils/translation';
import { setCurrentPage, setSelectedAgentId } from '@/store/account/slice';

const menuToRouteMap: Partial<Record<ResellerSiderItem, string>> = {
	[ResellerSiderItem.MY_RECEPTIONISTS]: AppRoutes.RESELLER_PORTAL,
	[ResellerSiderItem.ONBOARDING]: AppRoutes.RESELLER_ONBOARDING,
	[ResellerSiderItem.CUSTOM_DOMAIN]: AppRoutes.RESELLER_CUSTOM_DOMAIN,
	[ResellerSiderItem.EMBED]: AppRoutes.RESELLER_EMBED,
	[ResellerSiderItem.BRANDING]: AppRoutes.RESELLER_BRANDING,
	[ResellerSiderItem.SUBSCRIPTION]: AppRoutes.RESELLER_SUBSCRIPTION,
	[ResellerSiderItem.TRIAL_RECEPTIONIST]: AppRoutes.TRIAL_RECEPTIONIST,
	[ResellerSiderItem.PHONE_NUMBERS]: AppRoutes.RESELLER_PHONE_NUMBERS,
	[ResellerSiderItem.EMAIL_CONFIG]: AppRoutes.RESELLER_EMAIL_CONFIG,
	[ResellerSiderItem.BRANDING_CONFIG]: AppRoutes.RESELLER_EMAIL_CONFIG,
	[ResellerSiderItem.IMPORT_NUMBER]: AppRoutes.RESELLER_IMPORT_NUMBER,
	[ResellerSiderItem.ACADEMY]: AppRoutes.RESELLER_ACADEMY,
	[ResellerSiderItem.WEBHOOKS]: AppRoutes.RESELLER_WEBHOOKS,
	[ResellerSiderItem.CONTACT_SUPPORT]: AppRoutes.RESELLER_CONTACT_SUPPORT,
	[ResellerSiderItem.PLAN_CREATION]: AppRoutes.RESELLER_PLAN_CREATION,
	[ResellerSiderItem.PLAN_VIEW]: AppRoutes.RESELLER_PLAN_VIEW,
	[ResellerSiderItem.OUTBOUND_ROOT]: AppRoutes.RESELLER_OUTBOUND,
	[ResellerSiderItem.OUTBOUND_MY_CAMPAIGNS]: AppRoutes.RESELLER_OUTBOUND,
	[ResellerSiderItem.OUTBOUND_BYOT]: AppRoutes.RESELLER_OUTBOUND,
	[ResellerSiderItem.INTERCOM_SUPPORT]: AppRoutes.RESELLER_INTERCOM_SUPPORT,
	[ResellerSiderItem.MANAGE_CREDITS]: AppRoutes.RESELLER_MANAGE_CREDITS,
	[ResellerSiderItem.ADVANCED_SETTINGS]: AppRoutes.RESELLER_ADVANCED_SETTINGS,
};
const ResellerDashboard: FC<{
	children: ReactNode;
	activeKey: ResellerSiderItem;
	isPageLoading?: boolean;
	showHeader?: boolean;
}> = ({ children, activeKey, isPageLoading, showHeader = true }) => {
	const navigate = useNavigate();
	const currentReseller = useAppSelector(getResellerData);
	const [loading, setLoading] = useState<boolean>(true);
	const [collapsed, setCollapsed] = useState<boolean>(false);
	const dispatch = useDispatch();
	const dashboardTheme = useAppSelector((state) => state.app.dashboardTheme);
	const { isMobile } = useMobileDeviceCheck();

	const htmlElement = document.querySelector('html');
	const currentLang = htmlElement?.lang || 'en';

	// for auto-setting English language
	useEffect(() => {
		const setEnglishLanguage = async () => {
			try {
				// check for Google Translate cookie
				const match = document.cookie.match(/(^|;)\s*googtrans=([^;]+)/);
				if (match) {
					const langPair = decodeURIComponent(match[2]);
					const targetLang = langPair.split('/')[2];
					if (targetLang === 'en') {
						return; // already in English, no need to change
					}
				}

				// if not in English, apply English translation
				if (currentLang !== 'en') {
					await applyTranslation('en');
					document.documentElement.lang = 'en';
				}
			} catch (error) {
				console.error('Error setting English language:', error);
			}
		};

		if (!loading) {
			setEnglishLanguage();
		}
	}, [loading, currentLang]);

	useEffect(() => {
		if (isMobile) {
			setCollapsed(true);
		}
	}, [isMobile]);

	useEffect(() => {
		if (dashboardTheme) {
			dispatch(setAppTheme(dashboardTheme));
		} else if (currentReseller?.dashboardTheme) {
			dispatch(setAppTheme(currentReseller?.dashboardTheme));
		}
	}, [dashboardTheme]);

	const handleMenuSelect = (key: ResellerSiderItem) => {
		const route = menuToRouteMap[key];
		const outboundRoutes = ['my campaigns', 'byot'];
		if (route) {
			if (outboundRoutes.includes(key.toLowerCase())) {
				dispatch(setSelectedAgentId({ selectedAgentId: null }));
				dispatch(setCurrentPage(key as any));
				if (key === 'My Campaigns') {
					navigate(route);
				}
			} else {
				navigate(route);
			}
		}

		if (isMobile) {
			setCollapsed(true);
		}
	};

	const toggleSidebar = () => {
		setCollapsed(!collapsed);
	};

	// Close sidebar when clicking on overlay
	const handleOverlayClick = () => {
		if (isMobile && !collapsed) {
			setCollapsed(true);
		}
	};

	const { data: resellerLogo, isLoading } = useQuery({
		queryKey: ['reseller-logo'],
		queryFn: () => getResellerLogo({ key: currentReseller?.logo as string }),
		enabled: !!currentReseller?.logo,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const resellerLogoUrl = useMemo(() => {
		if (!resellerLogo) {
			return;
		}
		const logoUrl = URL.createObjectURL(resellerLogo);
		dispatch(updateReseller({ logoUrl }));
		return logoUrl;
	}, [resellerLogo]);

	const baseClass = 'reseller-dashboard-container';
	return (
		<>
			{currentReseller?.permanently_unbranded || currentReseller?.branded ? (
				''
			) : (
				<Alert
					className={`${baseClass}_reseller-promo`}
					description={<ResellerPromo />}
					closable={true}
				/>
			)}
			{currentReseller?.needPaymentClearance && (
				<Alert
					message="Important: Outstanding Balance"
					description={<ResellerPendingPaymentPromo />}
					type="warning"
					showIcon
				/>
			)}
			<ResellerSubCancelPromo />
			<Layout className={baseClass}>
				<ResellerSider
					setLoading={setLoading}
					activeMenuItem={activeKey}
					onMenuSelect={handleMenuSelect}
					hideOutbound={
						(currentReseller?.outboundSubscriptionId &&
							currentReseller?.outboundSubscriptionStatus &&
							currentReseller?.outboundSubscriptionStatus !== 'canceled') ||
						currentReseller?.allowOutboundAccess === true
							? false
							: true
					}
					collapsed={collapsed}
				/>
				{/* Overlay for mobile */}
				{isMobile && (
					<div
						className={`sider-overlay ${!collapsed ? 'visible' : ''}`}
						onClick={handleOverlayClick}
					/>
				)}
				{loading ? (
					<Loading />
				) : (
					<Layout className="content-layout">
						{showHeader && (
							<div className="px-4 pt-4 flex items-center gap-2">
								{isMobile && (
									<Button
										type="text"
										icon={<MenuOutlined />}
										onClick={toggleSidebar}
										className="hamburger-toggle"
									/>
								)}
								{isLoading ? (
									<Skeleton.Avatar size={'large'} shape="square" />
								) : resellerLogoUrl ? (
									<img
										className=" rounded-lg"
										src={resellerLogoUrl}
										style={{
											width: `${currentReseller?.logoDimensions?.width || 64}px`,
											height: `${currentReseller?.logoDimensions?.width || 64}px`,
											objectFit: 'contain',
										}}
										alt="logo"
									/>
								) : (
									''
								)}
								<span className="font-semibold text-2xl">{currentReseller?.businessName}</span>
							</div>
						)}

						<div className="page-content">{isPageLoading ? <Loading /> : children}</div>
					</Layout>
				)}
				<ClearPaymentModal />
			</Layout>
		</>
	);
};

export default ResellerDashboard;
